import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab, { tabClasses } from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DiagnosticsTabs } from '../schemas';
import PacingMetrics from './diagnostics/PacingMetrics';
import { PerformanceMonitoring } from './diagnostics/performanceMonitoring/PerformanceMonitoring';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`diagnostics-tabpanel-${index.toString()}`}
      aria-labelledby={`diagnostics-tab-${index.toString()}`}
      {...other}
    >
      {value === index ? <Box pt={3}>{children}</Box> : null}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `diagnostics-tab-${index.toString()}`,
    'aria-controls': `diagnostics-tabpanel-${index.toString()}`,
  };
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.25',
  borderRadius: 8,
  zIndex: 1,
  minHeight: 36,
  color: theme.palette.primary.dark,
  [`&.${tabClasses.selected}`]: {
    fontWeight: 500,
    color: theme.palette.primary.dark,
  },
  [`&.${tabClasses.root}`]: {
    padding: theme.spacing(0, 1.5),
  },
}));

const diagnosticsTabs: Record<DiagnosticsTabs, number> = {
  performanceMonitoring: 0,
  detailedDiagnostics: 1,
} as const;

const Diagnostics = () => {
  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });
  const { diag: diagnosticsTab } = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });
  const diagnosticsTabIndex = diagnosticsTabs[diagnosticsTab];

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTabIndex: number
  ) => {
    void navigate({
      to: '/algorithm-tools/starvation-bidder',
      search: (prev) => ({
        ...prev,
        page: 1,
        performanceStatus: undefined,
        diag: Object.keys(diagnosticsTabs)[newTabIndex],
        diagCampaignOrInsertionId: undefined,
        diagCampaignOrInsertionName: undefined,
        adGroupPage: 1,
        adGroupSearch: undefined,
      }),
      resetScroll: false,
    });
  };

  return (
    <Box>
      <Tabs
        value={diagnosticsTabIndex}
        onChange={handleTabChange}
        aria-label='diagnostics-tabs'
        sx={{
          backgroundColor: (theme) => theme.palette.primary[50],
          borderRadius: 1,
          px: 0.4,
          minHeight: 36,
        }}
        slotProps={{
          indicator: {
            sx: {
              backgroundColor: 'background.paper',
              height: '86%',
              top: '7%',
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.sm,
            },
          },
        }}
      >
        <StyledTab label='Performance Monitoring' {...a11yProps(0)} />
        <StyledTab label='Detailed Diagnostics' {...a11yProps(1)} />
      </Tabs>

      <TabPanel value={diagnosticsTabIndex} index={0}>
        <PerformanceMonitoring />
      </TabPanel>
      <TabPanel value={diagnosticsTabIndex} index={1}>
        <PacingMetrics />
      </TabPanel>
    </Box>
  );
};

export default Diagnostics;
